import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useSubmitSettlement from "../../apis/useSubmitSettlement/useSubmitSettlement";
import { TERMINAL_ENDPOINTS } from "../../apis/useTerminalAction/types";
import useTerminalAction from "../../apis/useTerminalAction/useTerminalAction";
import Overlay from "../../components/Overlay/Overlay";
import PopupDialog from "../../components/elements/PopupDialog/PopupDialog";
import { useConfigActions, useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { PATH } from "../../router/path";
import { addDatadogAction, buildLocationContext } from "../../services/datadog";
import "./MaintenancePage.scss";
import ReprintOrdersModal from "./ReprintOrdersModal";

type ActionKey = "logon" | "update" | "resetSequence" | "reset" | "settlement";

type ActionState =
	| { status: "idle" }
	| { status: "running"; actionLabel: string }
	| { status: "success"; actionLabel: string }
	| { status: "error"; actionLabel: string; message: string };

interface ITerminalAction {
	key: ActionKey;
	label: string;
	run: () => Promise<void>;
}

const MaintenancePage: React.FC = () => {
	const navigate = useNavigate();
	const { REVOKE_MAINTENANCE_ACCESS } = useConfigActions();
	const { state: maintenanceAccessGranted } = useConfigContext(
		(state) => state.maintenanceAccessGranted
	);
	const { state: location } = useConfigContext((state) => state.location);
	const { state: stall } = useConfigContext((state) => state.config.stall);

	const [actionState, setActionState] = useState<ActionState>({ status: "idle" });
	const [reprintOpen, setReprintOpen] = useState(false);
	const mountFiredRef = useRef(false);

	const logon = useTerminalAction(TERMINAL_ENDPOINTS.LOGON);
	const update = useTerminalAction(TERMINAL_ENDPOINTS.UPDATE);
	const resetSequence = useTerminalAction(TERMINAL_ENDPOINTS.RESET_SEQUENCE);
	const reset = useTerminalAction(TERMINAL_ENDPOINTS.RESET);
	const settlement = useSubmitSettlement();

	useEffect(() => {
		if (mountFiredRef.current) return;

		addDatadogAction("maintenance_page_mount", {
			...buildLocationContext(location, stall),
			hasAccess: !!maintenanceAccessGranted,
		});
		mountFiredRef.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!maintenanceAccessGranted) {
			addDatadogAction("maintenance_no_access_redirect", {
				...buildLocationContext(location, stall),
			});
			navigate(PATH.LANDING, { replace: true });
		}
	}, [maintenanceAccessGranted, navigate, location, stall]);

	const runAction = useCallback(
		async (action: ITerminalAction) => {
			if (actionState.status === "running") return;

			setActionState({ status: "running", actionLabel: action.label });
			addDatadogAction("maintenance_action_started", {
				...buildLocationContext(location, stall),
				actionLabel: action.key,
			});

			const startedAt = performance.now();

			try {
				await action.run();
				addDatadogAction("maintenance_action_success", {
					...buildLocationContext(location, stall),
					actionLabel: action.key,
					durationMs: performance.now() - startedAt,
				});
				setActionState({ status: "success", actionLabel: action.label });
			} catch (err) {
				const message = err instanceof Error ? err.message : "An unexpected error occurred";

				addDatadogAction("maintenance_action_failed", {
					...buildLocationContext(location, stall),
					actionLabel: action.key,
					errorMessage: message,
				});
				setActionState({ status: "error", actionLabel: action.label, message });
			}
		},
		[actionState.status, location, stall]
	);

	const dismissDialog = useCallback(() => {
		setActionState({ status: "idle" });
	}, []);

	const handleOpenReprint = useCallback(() => {
		addDatadogAction("reprint_list_opened", {
			...buildLocationContext(location, stall),
		});
		setReprintOpen(true);
	}, [location, stall]);

	const handleCloseReprint = useCallback(() => {
		setReprintOpen(false);
	}, []);

	const handleGoBack = useCallback(() => {
		addDatadogAction("maintenance_back_to_landing", {
			...buildLocationContext(location, stall),
		});
		REVOKE_MAINTENANCE_ACCESS();
		navigate(PATH.LANDING);
	}, [REVOKE_MAINTENANCE_ACCESS, navigate, location, stall]);

	const actions: ITerminalAction[] = useMemo(
		() => [
			{
				key: "logon",
				label: "Nets Logon",
				run: async () => {
					await logon.submit();
				},
			},
			{
				key: "update",
				label: "Nets Update",
				run: async () => {
					await update.submit();
				},
			},
			{
				key: "resetSequence",
				label: "Nets Reset Sequence",
				run: async () => {
					await resetSequence.submit();
				},
			},
			{
				key: "reset",
				label: "Nets Reboot",
				run: async () => {
					await reset.submit();
				},
			},
			{
				key: "settlement",
				label: "Nets Settlement",
				run: async () => {
					await settlement.submit();
				},
			},
		],
		[logon, update, resetSequence, reset, settlement]
	);

	if (!maintenanceAccessGranted) return null;

	const isRunning = actionState.status === "running";

	return (
		<div className="MaintenancePage">
			<div className="MaintenancePage__container">
				<div className="MaintenancePage__header">
					<h1 className="MaintenancePage__title">Maintenance Panel</h1>
				</div>

				<div className="MaintenancePage__actions">
					{actions.map((action) => (
						<button
							key={action.key}
							className="MaintenancePage__action-btn"
							data-testid={`maintenance-action-${action.key}`}
							onClick={() => runAction(action)}
							disabled={isRunning}
							type="button"
						>
							{action.label}
						</button>
					))}
					<button
						className="MaintenancePage__action-btn"
						data-testid="maintenance-reprint-orders"
						onClick={handleOpenReprint}
						disabled={isRunning}
						type="button"
					>
						RE-PRINT
					</button>
				</div>

				<div className="MaintenancePage__footer">
					<button
						className="MaintenancePage__back-btn"
						onClick={() => window.location.reload()}
						type="button"
					>
						Reload App
					</button>
					<button className="MaintenancePage__back-btn" onClick={handleGoBack} type="button">
						Back to App
					</button>
				</div>
			</div>

			{isRunning && (
				<Overlay
					line1={`${actionState.actionLabel} in progress...`}
					line2="Please wait, do not close this page."
					enableDotAnimation={true}
				/>
			)}

			<PopupDialog
				data-testid="maintenance-success-dialog"
				className="MaintenanceResultDialog"
				open={actionState.status === "success"}
				title={`${actionState.status === "success" ? actionState.actionLabel : ""} Success`}
				content={<span>The action completed successfully.</span>}
				buttons={[
					{
						text: "OK",
						onClick: dismissDialog,
						className: "BtnMaintenanceOk",
					},
				]}
			/>

			<PopupDialog
				data-testid="maintenance-error-dialog"
				className="MaintenanceResultDialog"
				open={actionState.status === "error"}
				title={`${actionState.status === "error" ? actionState.actionLabel : ""} Failed`}
				content={<span>{actionState.status === "error" ? actionState.message : ""}</span>}
				buttons={[
					{
						text: "OK",
						onClick: dismissDialog,
						className: "BtnMaintenanceOk",
					},
				]}
			/>
			<ReprintOrdersModal open={reprintOpen} onClose={handleCloseReprint} />
		</div>
	);
};

export default MaintenancePage;
